import { KeyboardEvent } from 'react';
import cn from 'classnames';
import { creatorbase } from '@round/api';
import { Card, Skeleton } from '@round/creatorbase-ui';
import { formatDate } from 'helpers';
import BulkUploadStatusBadge from '../BulkUploadStatusBadge/BulkUploadStatusBadge';
import styles from './BulkUploadCard.module.css';

type Props = {
    upload: creatorbase.CreatorBulkUpload;
    /** Makes the card a button, for a list the upload is picked from. */
    onSelect?: () => void;
};

/**
 * One upload: when it was submitted, how much of it was saved and where it got to. The same card is
 * the upload's row in Results and the summary over its results, so the two read as the same thing.
 *
 * Card lays its children out in a column, so the line lives inside a single Card.Content rather than
 * being several direct children of the Card.
 */
const BulkUploadCardRoot = ({ upload, onSelect }: Props) => {
    const handleKeyDown = (event: KeyboardEvent) => {
        if (['Enter', ' ', 'Spacebar'].includes(event.key)) {
            event.preventDefault();
            onSelect?.();
        }
    };

    const buttonProps = onSelect ? { role: 'button', tabIndex: 0, onClick: onSelect, onKeyDown: handleKeyDown } : {};

    return (
        <Card size="sm" className={cn({ [styles.selectable]: onSelect })} {...buttonProps}>
            <Card.Content className={styles.content}>
                <span className={styles.submitted}>
                    {formatDate(upload.created, { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className={styles.saved}>
                    {upload.succeeded_count}/{upload.total_items} saved
                </span>
                {/* A clean upload says nothing about failures rather than carrying a zero. */}
                {upload.failed_count > 0 && <span className={styles.failed}>{upload.failed_count} failed</span>}
                <BulkUploadStatusBadge status={upload.status} className={styles.status} />
            </Card.Content>
        </Card>
    );
};

/** A BulkUploadCard whose upload has not arrived yet. */
const BulkUploadCardSkeleton = () => (
    <Card size="sm">
        <Card.Content>
            <Skeleton className={styles.skeleton} />
        </Card.Content>
    </Card>
);

const BulkUploadCard = Object.assign(BulkUploadCardRoot, {
    Skeleton: BulkUploadCardSkeleton,
});

export default BulkUploadCard;
